<script setup lang="ts">
import { register } from '@/api/auth'
import { ApiError } from '@/api/client'
import ProviderAgreementDialog from '@/components/ProviderAgreementDialog.vue'
import { countries } from '@/data/countries'
import { useAuthStore } from '@/stores/auth'
import type { Provider, ProviderAccountType, ProviderUser } from '@/types'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  meta: { layout: 'blank', public: true, publicOnly: true },
})

const router = useRouter()
const auth = useAuthStore()

/*
 * Se bumpea cuando cambia el agreement, para saber quién aceptó qué versión.
 *
 * 1.1 — cláusula 5: datos de clientes y de salud (el cuestionario médico es
 * dato del art. 9 GDPR y el agreement no lo mencionaba). Mantener en sync con
 * el encabezado de `data/provider-agreement.md`.
 */
const TERMS_VERSION = '1.1'

const email = ref('')
const code = ref('')
const accountType = ref<ProviderAccountType>('individual')
const acceptedTerms = ref(false)
const agreementOpen = ref(false)

const firstName = ref('')
const lastName = ref('')
const password = ref('')
const confirmPassword = ref('')
const isPasswordVisible = ref(false)

const displayName = ref('')
const taxId = ref('')
const country = ref('')
const phone = ref('')
const website = ref('')

const loading = ref(false)
const error = ref('')

const passwordRulesOk = computed(() => password.value.length >= 6 && /[A-Z]/.test(password.value))
const passwordsMatch = computed(() => !!password.value && password.value === confirmPassword.value)

onMounted(() => {
  email.value = sessionStorage.getItem('bt_register_email') || ''
  code.value = sessionStorage.getItem('bt_register_code') || ''
  accountType.value = (sessionStorage.getItem('bt_register_account_type') as ProviderAccountType) || 'individual'

  // Sin email + código verificado no hay registro posible. A login y no a
  // `register-email`: con el alta cerrada ese paso rebota igual, y quien llegó
  // por el link de la invitación nunca lo recorrió.
  if (!email.value || !code.value)
    router.replace({ name: 'login' })
})

const submit = async () => {
  error.value = ''
  if (!passwordRulesOk.value) {
    error.value = 'Password must be at least 6 characters and include one uppercase letter.'

    return
  }
  if (!passwordsMatch.value) {
    error.value = 'Passwords do not match.'

    return
  }
  if (accountType.value === 'company' && !displayName.value.trim()) {
    error.value = 'Company name is required.'

    return
  }
  if (!acceptedTerms.value) {
    error.value = 'You must accept the Terms of Service and Privacy Policy.'

    return
  }

  loading.value = true
  try {
    const session = await register({
      email: email.value,
      code: code.value,
      password: password.value,
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      accountType: accountType.value,
      displayName: displayName.value.trim() || undefined,
      taxId: accountType.value === 'company' ? taxId.value.trim() || undefined : undefined,
      country: country.value?.trim() || undefined,
      phone: phone.value.trim() || undefined,
      website: website.value.trim() || undefined,
      acceptedTerms: acceptedTerms.value,
      termsVersion: TERMS_VERSION,
    })

    // `AuthSession` es una vista slim de Provider/ProviderUser; el dato completo
    // llega enseguida por /account/me (mismo criterio que en login).
    auth.setSession(
      session.provider as unknown as Provider,
      session.providerUser as unknown as ProviderUser,
      session.token.accessToken,
      session.token.refreshToken,
    )

    sessionStorage.removeItem('bt_register_email')
    sessionStorage.removeItem('bt_register_code')
    sessionStorage.removeItem('bt_register_account_type')

    // `replace` evita volver al setup con el botón atrás.
    await router.replace({ name: 'dashboard' })
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Registration failed.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper"
    no-gutters
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Set up your account 🚀
          </h4>
          <p class="mb-0">
            {{ accountType === 'company' ? 'Tell us about your business.' : 'Tell us about you and your business.' }}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="submit">
            <VRow>
              <VCol
                v-if="error"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                  density="compact"
                >
                  {{ error }}
                </VAlert>
              </VCol>

              <VCol cols="12">
                <div class="text-overline text-medium-emphasis">
                  Your details
                </div>
              </VCol>

              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="firstName"
                  autofocus
                  label="First name"
                  :disabled="loading"
                />
              </VCol>

              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="lastName"
                  label="Last name"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="password"
                  label="Password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  hint="6+ characters, with at least one uppercase letter."
                  persistent-hint
                  :disabled="loading"
                  :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="confirmPassword"
                  label="Confirm password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <div class="text-overline text-medium-emphasis">
                  {{ accountType === 'company' ? 'Your company' : 'Your business' }}
                </div>
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="displayName"
                  :label="accountType === 'company' ? 'Company name' : 'Commercial name (optional)'"
                  :placeholder="accountType === 'individual' ? 'Defaults to your full name' : ''"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VAutocomplete
                  v-model="country"
                  :items="countries"
                  item-title="name"
                  item-value="name"
                  label="Country"
                  autocomplete="off"
                  clearable
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="phone"
                  label="Phone"
                  type="tel"
                  :disabled="loading"
                />
              </VCol>

              <VCol
                v-if="accountType === 'company'"
                cols="12"
              >
                <VTextField
                  v-model="taxId"
                  label="Tax ID / VAT (optional)"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="website"
                  label="Website (optional)"
                  type="url"
                  placeholder="https://"
                  :disabled="loading"
                />
              </VCol>

              <!-- El agreement debe leerse y aceptarse antes de crear la cuenta;
                   la aceptación se registra en DB (termsAcceptedAt + termsVersion). -->
              <VCol cols="12">
                <VSheet
                  border
                  rounded
                  class="pa-4 d-flex align-center flex-wrap ga-3"
                >
                  <VIcon
                    :icon="acceptedTerms ? 'ri-checkbox-circle-line' : 'ri-file-text-line'"
                    :color="acceptedTerms ? 'success' : 'medium-emphasis'"
                  />
                  <div class="flex-grow-1 text-body-2">
                    <template v-if="acceptedTerms">
                      You've accepted the Provider Agreement.
                    </template>
                    <template v-else>
                      Please read and accept the Provider Agreement to continue.
                    </template>
                  </div>
                  <VBtn
                    variant="tonal"
                    size="small"
                    :disabled="loading"
                    @click="agreementOpen = true"
                  >
                    {{ acceptedTerms ? 'View agreement' : 'Read agreement' }}
                  </VBtn>
                </VSheet>
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                  :loading="loading"
                  :disabled="!acceptedTerms"
                >
                  Create account
                </VBtn>
              </VCol>

              <VCol
                cols="12"
                class="text-center text-base"
              >
                <span>Already have an account?</span>
                <RouterLink
                  class="text-primary d-inline-block ms-1"
                  :to="{ name: 'login' }"
                >
                  Log in
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>

          <ProviderAgreementDialog
            v-model="agreementOpen"
            :accepted="acceptedTerms"
            @accept="acceptedTerms = true"
          />
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
